import { computed, effect, inject, Injectable, Injector, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  CheckoutExcursionOut,
  CheckoutFormOut,
  CheckoutHotelExtensionOut,
  CheckoutHotelOut,
  CheckoutHotelSpecialRequestsIn,
  CheckoutTouristIn,
  CheckoutTransferTypeEnum,
  Country,
  FlightSeatsWarning,
  IdentifierTypeEnum,
  ManualFlightInfo,
  PackageFlight,
  PackageHotel,
  Price,
  PriceTypeEnum,
  Terminal,
  TourPackage
} from '@appTypes/api.types';
import { ApiErrorResponse } from '@core/interceptors/api.interceptor';
import {
  FIN_MESSAGE,
  FIN_PATTERN,
  justNumbers,
  maxDateThanField,
  minDate,
  minDateThanField,
  normalizeFlightTime,
  regExpPattern,
  requiredLength,
  timeMaskFormat
} from '@core/misc/validators.utils';
import { UserState } from '@core/modules/user/state/user.state';
import { DataLoadStatusModel } from '@core/store/store.models';
import { startOfTomorrow } from 'date-fns';
import { GENDERS_DATA } from '@data/common.data';
import { environment } from '@env/environment';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, createDispatchMap, createSelectMap, ofActionCompleted, Store } from '@ngxs/store';
import { instantLostText } from '@shared/components/instant-confirmation/instant-confirmation.utils';
import {
  CheckoutExtensionChoices,
  ECI_LANDING_UNKNOWN,
  EARLY_CHECK_IN,
  EXT_PRICE_CHANGED,
  ExtensionRefusal,
  extensionRefusalOf,
  offerOf,
  offersOf,
  withExtensions
} from '@shared/modules/hotel-extensions/hotel-extensions.utils';
import {
  HotelExtensionChangeChoice,
  HotelExtensionChangeData,
  HotelExtensionPriceChangedDialogComponent
} from '@shared/modules/hotel-extensions/hotel-extension-price-changed-dialog.component';
import { cartClear, cartRemoveItem } from '@shared/modules/cart/state/cart.actions';
import { CartState } from '@shared/modules/cart/state/cart.state';
import { ConfirmService } from '@shared/modules/confirm/confirm.service';
import { ConfirmType } from '@shared/modules/confirm/confirm.types';
import { SpecialRequestsDraftService } from '@shared/components/special-requests/special-requests-draft.service';
import {
  SpecialRequestsNotSavedData,
  SpecialRequestsNotSavedDialogComponent
} from '@shared/components/special-requests/special-requests-not-saved-dialog.component';
import { DynamicModalService } from '@shared/modules/dynamic-modal/dynamic-modal.service';
import { CommonHelpersService } from '@shared/services/common-helpers.service';
import { ShownMoneyService } from '@shared/services/shown-money.service';
import { extraServiceErrorText } from '@shared/utils/extra-service-errors';
import { terminalOptions } from '@shared/utils/terminal-label';
import { shownPriceRows } from '../checkout-shown-prices';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  firstValueFrom,
  map,
  of,
  skip,
  startWith,
  switchMap,
  take,
  takeWhile,
  tap,
  throwError
} from 'rxjs';
import { appGetCountries, appGoPreviousPage } from '@app-state/app.actions';
import { AppState } from '@app-state/app.state';
import { FLIGHT_PRICE_CHANGED, aviaFlightKey } from '@pages/avia-page/avia-fares';
import {
  AviaHoldRefusedNoticeComponent,
  AviaHoldRefusedNoticeData
} from '@pages/avia-page/components/avia-hold-refused-notice/avia-hold-refused-notice.component';
import {
  AviaPriceChangedChoice,
  AviaPriceChangedDialogComponent,
  AviaPriceChangedDialogData
} from '@pages/avia-page/components/avia-price-changed-dialog/avia-price-changed-dialog.component';
import { AviaFareReturnService } from '@pages/avia-page/services/avia-fare-return.service';
import { TourFareReturnService } from '@pages/tours-page/services/tour-fare-return.service';
import {
  FLIGHT_PRICE_OVERBOOKED,
  FLIGHT_SEATS_ON_REQUEST,
  hasOnRequestWarning,
  isNoSeatsRefusal,
  isOnRequestNotAccepted,
  isTourOnRequest,
  isRegisteredAnswer,
  noSeatsTextParams,
  ON_REQUEST_NO_LONGER_TEXT,
  ON_REQUEST_NOT_ACCEPTED_TEXT,
  onRequestWarningTextParams,
  OVER_BLOCK_LIMIT_REASON,
  refusedTour,
  TOUR_NO_SEATS_DETAILS_KEY
} from '@pages/tours-page/utils/tour-seat-shortage';
import {
  CHECKOUT_FLIGHTS_TABLE_COLUMNS,
  CHECKOUT_HOTELS_TABLE_COLUMNS,
  CHECKOUT_INSURANCES_TABLE_COLUMNS,
  CHECKOUT_PRICE_TABLE_COLUMNS,
  CHECKOUT_TRANSFERS_AND_VISAS_TABLE_COLUMNS
} from '../checkout.const';
import {
  CheckoutHotelTypedForm,
  CheckoutTouristTransferTypedForm,
  CheckoutTouristTypedForm,
  CheckoutTypedForm,
  ICheckoutFormValues,
  ICheckoutHotelFormValues,
  ICheckoutTouristFormValues
} from '../checkout.typed-forms';
import {
  ICartHotelTransfer,
  carsOf,
  legOf,
  sameCar
} from '@pages/hotels-page/components/hotel-details-transfer/hotel-details-transfer.logic';
import {
  CalcCheckoutPayload,
  CheckoutInstantLostItem,
  CheckoutPriceItem,
  ICheckoutFlightsData,
  ICheckoutHotelData,
  ICheckoutChosenTransfer,
  ICheckoutTransferExtras,
  ICheckoutTransfersData,
  ITouristWithInsuranceData
} from '../checkout.types';
import {
  CheckoutTouristCheckInConfigFormComponent,
  CheckoutTouristCheckInConfigParams
} from '../components/checkout-tourist-check-in-config-form/checkout-tourist-check-in-config-form.component';
import {
  CheckoutTransferAndVisaFormComponent,
  CheckoutTransferAndVisaParams,
  CheckoutTransferAndVisaSaveData
} from '../components/checkout-transfer-and-visa-form/checkout-transfer-and-visa-form.component';
import {
  calcCheckout,
  checkOutClearFormData,
  checkoutVerifyCalculatedPrices,
  confirmCheckoutFormData
} from '../state/checkout.actions';
import { CheckoutState } from '../state/checkout.state';

/** CHECKOUT-4: the tourist codes of packages-api's price check (CheckoutService.ValidateTourist / ValidatePassport) with our text; others show the server's. */
const TOURIST_CHECK_TEXTS: Record<string, string> = {
  PASSPORT_ISSUE_DATE_REQUIRED: 'Tourist {n}: enter the passport issue date.',
  PASSPORT_ISSUE_DATE_IS_INCORRECT: 'Tourist {n}: the passport issue date cannot be later than today.',
  TIN_REQUIRED: 'Tourist {n}: enter the taxpayer number (TIN).',
  BIRTH_DATE_IS_INCORRECT: 'Tourist {n}: check the date of birth.'
};

/** The dialog shows its message as HTML: a server text (it may repeat a typed name) is escaped first. */
const escapeHtml = (text: string): string =>
  text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);

@Injectable()
export class CheckoutService {
  // #region Injections & State

  readonly #injector = inject(Injector);
  readonly #dynamicModalService = inject(DynamicModalService);
  /** ADLC-7 (booking-special-requests qa.md R-SR6 .. R-SR13): created by the hotel cart on first use; null = no line was drawn. */
  #specialRequests: SpecialRequestsDraftService | null = null;
  /** R-SR19f: Register is on its way WITH special requests - the button reads "Saving…" with `aria-busy` (never creates the cart service). */
  public readonly specialRequestsSaving = signal(false);

  /** The special requests of this checkout visit - one instance, shared with the hotel cart lines. */
  public get specialRequests(): SpecialRequestsDraftService {
    this.#specialRequests ??= Injector.create({
      providers: [SpecialRequestsDraftService],
      parent: this.#injector
    }).get(SpecialRequestsDraftService);
    return this.#specialRequests;
  }
  readonly #store = inject(Store);
  readonly #confirmService = inject(ConfirmService);
  readonly #translate = inject(TranslateService);
  readonly #commonHelpersService = inject(CommonHelpersService);
  readonly #actions = inject(Actions);
  readonly #fareReturn = inject(AviaFareReturnService);
  readonly #tourFareReturn = inject(TourFareReturnService);
  readonly #money = inject(ShownMoneyService);

  readonly #storeActions = createDispatchMap({
    navigate: Navigate,
    getCountries: appGetCountries,
    calcCheckout: calcCheckout,
    goPreviousPage: appGoPreviousPage,
    verifyCalculatedPrices: checkoutVerifyCalculatedPrices,
    clearFormData: checkOutClearFormData,
    cartClear: cartClear,
    cartRemoveItem: cartRemoveItem,
    confirmCheckoutFormData: confirmCheckoutFormData
  });

  readonly #selectors = createSelectMap({
    countries: AppState.countries,
    terminals: CheckoutState.terminals,
    itemsCount: CartState.itemsCount,
    cartItems: CartState.items,
    cartHotels: CartState.hotelsFromCart,
    cartFlights: CartState.flightsFromCart,
    packageTypeAndCountry: CartState.packageTypeAndCountry,
    verifyCalculatedPricesState: CheckoutState.verifyCalculatedPricesState,
    calcCheckoutState: CheckoutState.calcCheckoutLoadState
  });

  readonly #checkoutFormOut = signal<CheckoutFormOut | null>(null);
  readonly #destroyListeners = signal<boolean>(false);

  // #endregion

  // #region Public Properties

  public readonly isReady = signal<boolean>(false);
  /** Charter seat selection (phase 3): the seat step's hold token, sold with the reservation at Confirm (R-41). */
  public readonly seatHoldToken = signal<string | null>(null);
  public readonly flightsIsRequired = signal<Record<string, boolean>>({});
  public readonly hotelErrors = signal<Record<string, string>>({});
  public readonly refreshPriceResultError = signal<string>('');
  /**
   * Set when an automatic price/tourist verification (`#verifyPrices`) comes back with
   * `error`/`containsError` - e.g. `CHECKOUT_INVALID_BIRTH_DATE`. Non-empty keeps "Register the
   * reservation" disabled (CHECKOUT-3); cleared as soon as a later verification succeeds.
   */
  public readonly priceVerificationError = signal<string>('');
  /** The refused verification's `activityId` - drawn as "Trace ID [Copy]" under that line (C23; docs#314: the line, never a window). */
  public readonly priceVerificationTraceId = signal<string | null>(null);

  // #region Hotel instant confirmation (QA doc docs features/hotel-instant-confirmation/qa.md, R-8, R-9, R-10)

  /** The last refresh's answer per hotel price id (CalcCheckout / verification / ConfirmCheckout `hotels[]`). */
  readonly #instantHotels = signal<Record<string, Pick<CheckoutHotelOut, 'instantConfirmation' | 'instantLost'>>>({});
  /** R-10: the hotels the agent chose "Continue on request" for - sent as `continueOnRequest` at Confirm. */
  public readonly instantContinueOnRequest = signal<Record<string, boolean>>({});
  /** The hotels still instant after the refresh - the order summary shows the chip for them. */
  public readonly instantTag = computed<Record<string, boolean>>(() => {
    const tag: Record<string, boolean> = {};
    for (const [id, h] of Object.entries(this.#instantHotels()))
      if (h.instantConfirmation && !h.instantLost) tag[id] = true;
    return tag;
  });
  /** R-8: the hotels that lost instant confirmation and wait for the agent's choice. */
  public readonly instantLostHotels = computed<CheckoutInstantLostItem[]>(() => {
    const continued = this.instantContinueOnRequest();
    const cartHotels = untracked(() => this.#selectors.cartHotels());
    return Object.entries(this.#instantHotels())
      .filter(([id, h]) => !!h.instantLost && !continued[id])
      .map(([id, h]) => ({
        hotelPriceId: id,
        hotelName: cartHotels.find((c) => c?.hotelPriceId === id)?.hotelName ?? null,
        errorCode: h.instantLost!.errorCode,
        reason: h.instantLost!.reason,
        text: instantLostText(h.instantLost!.reason, h.instantLost!.message)
      }));
  });
  /** R-10: the hotels the agent continued on request for (the note above the order). */
  public readonly instantOnRequestHotels = computed<string[]>(() => {
    const continued = this.instantContinueOnRequest();
    return Object.entries(this.#instantHotels())
      .filter(([id, h]) => !!h.instantLost && continued[id])
      .map(([id]) => id);
  });
  /** R-8 / C3: "Register the reservation" stays disabled, with the reason, until the agent chooses. */
  public readonly instantRegisterBlocked = computed(() => this.instantLostHotels().length > 0);

  // #endregion

  // #region ECI/LCO V1 (docs features/hotel-extensions; spec section 4 "Agency during booking")

  /** The last answer's extensions per hotel price id - absent while the feature is off, so nothing is shown. */
  readonly #extensionOffers = signal<Record<string, CheckoutHotelExtensionOut[]>>({});
  public readonly extensionOffers = this.#extensionOffers.asReadonly();
  /** What the agent ticked per hotel and kind (the quote id the answer gave, the price accepted after EXT_PRICE_CHANGED). */
  public readonly extensionChoices = signal<CheckoutExtensionChoices>({});
  /** The hotels with something to offer, in cart order, with their names. */
  public readonly extensionHotels = computed(() => {
    const offers = this.#extensionOffers();
    return this.#selectors
      .cartHotels()
      .filter((h) => !!h?.hotelPriceId && !!offers[h.hotelPriceId]?.length)
      .map((h) => ({ hotelPriceId: h.hotelPriceId!, hotelName: h.hotelName ?? null, offers: offers[h.hotelPriceId!] }));
  });
  /** ECI_LANDING_UNKNOWN on a ticked early check-in: these tourists get their Arrival block to type the flight (R-CK21 shape). */
  readonly #landingTourists = computed<Set<string>>(() => {
    const ids = new Set<string>();
    const choices = this.extensionChoices();
    for (const [hotelPriceId, offers] of Object.entries(this.#extensionOffers()))
      if (choices[hotelPriceId]?.[EARLY_CHECK_IN] && offerOf(offers, EARLY_CHECK_IN)?.code === ECI_LANDING_UNKNOWN)
        for (const hotel of this.formGroup.controls.hotels.controls)
          if (hotel.controls.hotelPriceId.value === hotelPriceId)
            for (const t of hotel.controls.touristIds.value ?? []) if (t) ids.add(t);
    return ids;
  });

  /** Tick / untick one extension: the hotel is priced again, so the answer carries the night's price, terms and quote. */
  public chooseExtension(hotelPriceId: string, kind: number, on: boolean): void {
    this.extensionChoices.update((all) => {
      const hotel = { ...(all[hotelPriceId] ?? {}) };
      if (on) hotel[kind] = hotel[kind] ?? {};
      else delete hotel[kind];
      return { ...all, [hotelPriceId]: hotel };
    });
    this.#updateValidators();
    this.#reCalcCheckout().subscribe();
  }

  /** After typing the arrival flight: the extensions are evaluated again with it. */
  public recheckExtensions(): void {
    this.#reCalcCheckout().subscribe();
  }

  #withExtensionChoice<T extends { hotelPriceId?: string | null }>(hotels: T[] | null | undefined): T[] {
    return withExtensions(hotels, this.extensionChoices(), this.#extensionOffers());
  }

  /** The answer's offers; a ticked kind takes its new quote id, and a kind refused for good is unticked (the reason stays on screen). */
  #applyExtensionState(response: CheckoutFormOut | null | undefined): void {
    if (!response?.hotels) return;
    const before = this.#landingTourists();
    const offers = offersOf(response.hotels);
    this.#extensionOffers.set(offers);
    this.extensionChoices.update((all) => {
      const next: CheckoutExtensionChoices = {};
      for (const [hotelPriceId, kinds] of Object.entries(all)) {
        const kept: CheckoutExtensionChoices[string] = {};
        for (const [k, choice] of Object.entries(kinds)) {
          const offer = offerOf(offers[hotelPriceId], Number(k));
          if (!offer || offer.mandatory || (!offer.eligible && offer.code !== ECI_LANDING_UNKNOWN)) continue;
          kept[Number(k)] = { ...choice, quoteId: offer.quoteId ?? choice.quoteId ?? null };
        }
        if (Object.keys(kept).length) next[hotelPriceId] = kept;
      }
      return next;
    });
    const after = this.#landingTourists();
    if (after.size !== before.size || [...after].some((t) => !before.has(t))) this.#updateValidators();
  }

  /** EXT_PRICE_CHANGED or another extension code at Confirm: nothing was registered. */
  #extensionRefusal(response: CheckoutFormOut | null | undefined): ExtensionRefusal | null {
    return extensionRefusalOf(response, this.extensionChoices(), this.#extensionOffers());
  }

  /** Accept the new price (Confirm again with it), book without the night (only when the agent says so), or stay - the spec's FLIGHT_PRICE_CHANGED flow. */
  #askExtensionChange(refusal: ExtensionRefusal, activityId?: string | null): void {
    const offer = offerOf(this.#extensionOffers()[refusal.hotelPriceId], refusal.kind);
    const data: HotelExtensionChangeData = {
      code: refusal.code,
      kind: refusal.kind,
      hotelName: this.#selectors.cartHotels().find((h) => h?.hotelPriceId === refusal.hotelPriceId)?.hotelName ?? null,
      nightFrom: offer?.nightFrom ?? null,
      nightTo: offer?.nightTo ?? null,
      oldSell: refusal.oldSell,
      newSell: refusal.newSell,
      currency: refusal.currency,
      canGoWithout: true,
      activityId
    };
    const ref = this.#dynamicModalService.open({
      component: HotelExtensionPriceChangedDialogComponent,
      data,
      width: '520px',
      maxWidth: '94vw',
      height: 'auto',
      mode: 'box',
      disableClose: true,
      dialogConfig: { ariaLabelledBy: 'hotel-extension-changed-title' }
    });
    ref.closed.pipe(take(1)).subscribe((choice?: HotelExtensionChangeChoice) => {
      if (choice === 'accept' && refusal.code === EXT_PRICE_CHANGED) {
        this.extensionChoices.update((all) => ({
          ...all,
          [refusal.hotelPriceId]: {
            ...(all[refusal.hotelPriceId] ?? {}),
            [refusal.kind]: { quoteId: refusal.quoteId, acceptedSell: refusal.newSell }
          }
        }));
        this.onSubmit();
      } else if (choice === 'without') {
        this.extensionChoices.update((all) => {
          const hotel = { ...(all[refusal.hotelPriceId] ?? {}) };
          delete hotel[refusal.kind];
          return { ...all, [refusal.hotelPriceId]: hotel };
        });
        this.onSubmit();
      }
    });
  }

  // #endregion

  // #region Flight on request (ADLC-1 slice 2b, QA doc docs features/flight-block-seat-shortage/README.md R-SS7, R-SS7a, SA-F6)

  /** The first cart tour whose flight online-api sells on request (`bookable: true` + SeatsOnRequest, fail closed); null = dormant. */
  public readonly onRequestTour = computed<TourPackage | null>(
    () =>
      (this.#selectors.cartItems() ?? [])
        .filter((item) => item.type === 'tour')
        .map((item) => item.data)
        .find((tour) => isTourOnRequest(tour)) ?? null
  );
  /** A ConfirmCheckout refused FLIGHT_SEATS_ON_REQUEST_NOT_ACCEPTED: its warning keeps the step on even when nothing else announced it. */
  readonly #onRequestRefusal = signal<FlightSeatsWarning | null>(null);
  /** The acceptance step is shown: a cart tour on request, or CalcCheckout `flightSeatsWarning.code = FLIGHT_SEATS_ON_REQUEST`. */
  public readonly onRequestStep = computed<boolean>(
    () => !!this.onRequestTour() || hasOnRequestWarning(this.#checkoutFormOut()) || !!this.#onRequestRefusal()
  );
  /** `{flight}`, `{free}`, `{n}` of the warning: the cart tour (or the first cart flight), the live CalcCheckout figures winning. */
  public readonly onRequestWarningParams = computed(() => {
    const tours = (this.#selectors.cartItems() ?? [])
      .filter((item) => item.type === 'tour' || item.type === 'avia')
      .map((item) => item.data as Pick<TourPackage, 'flight' | 'hotel'>);
    const holder = this.onRequestTour() ?? tours.find((t) => !!t?.flight) ?? null;
    const calcWarning = this.#checkoutFormOut()?.flightSeatsWarning;
    const warning = hasOnRequestWarning(this.#checkoutFormOut()) ? calcWarning : this.#onRequestRefusal();
    return onRequestWarningTextParams(holder, warning);
  });
  /** The agent ticked "I understand ..." - false at every page load (a reload starts unticked), never persisted. */
  public readonly onRequestAccepted = signal<boolean>(false);
  /** Register stays disabled, with the reason button, until the tick (C3). */
  public readonly onRequestRegisterBlocked = computed<boolean>(() => this.onRequestStep() && !this.onRequestAccepted());
  /** Set by checkout-content: scrolls to the box, pulses it and focuses the checkbox. */
  public onRequestFocusFn: (() => void) | null = null;

  public setOnRequestAccepted(accepted: boolean): void {
    this.onRequestAccepted.set(accepted);
  }

  // #endregion

  // #region Flight price guard (QA doc docs features/flight-fare-picker/qa.md R-FP12 / R-FP13 + processes/flight-booking-errors.md R-FH16, R-FH19)

  /** The per-flight totals the agent last saw (CalcCheckout / verification `flightTotals`); empty when the backend sends none. */
  readonly #aviaTotalsSeen = signal<Record<string, number>>({});
  /** The new totals the agent accepted in "The price has changed" - they win over what was seen. */
  readonly #aviaTotalsAccepted = signal<Record<string, number>>({});
  /** R-FH16: what each flight request carries as `expectedTotalPrice` at Confirm. */
  readonly #expectedFlightTotals = computed<Record<string, number>>(() => ({
    ...this.#aviaTotalsSeen(),
    ...this.#aviaTotalsAccepted()
  }));

  // #endregion

  public readonly personIdentifierValidationsIsDisabled = computed(
    () => this.#checkoutFormOut()?.disablePersonIdentifierValidations
  );
  public readonly requiredCitizenIdentifiers = computed(
    () => this.#checkoutFormOut()?.requiredCitizenIdentifiers ?? []
  );

  public readonly simplifiedFormForCitizenshipOf = computed(() => {
    const simplifiedFormCountries = this.#checkoutFormOut()?.simplifiedFormCountries;
    if (!simplifiedFormCountries) return [];
    return untracked(() => {
      const { packageType, destinationCountryCode } = this.#selectors.packageTypeAndCountry();
      return (
        simplifiedFormCountries?.filter(
          (e) => e.applicablePackageTypes?.includes(packageType!) && e.countryCode === destinationCountryCode
        ) ?? []
      );
    });
  });

  public readonly pricesTableColumns = CHECKOUT_PRICE_TABLE_COLUMNS;
  public readonly priceData = computed(() => {
    const prices = this.#checkoutFormOut()?.prices ?? [];
    // R-HT22: the rows in the shown currency, the Total the sum of the shown rows (null rates = the pipe converts as before)
    const target = this.#money.target(this.#checkoutFormOut()?.priceCurrency);
    const result = untracked(() => this.#mapPricesData(prices));
    const legs = this.chosenTransfers().map((leg) => leg.price);
    return target ? shownPriceRows(result, target, legs) : result;
  });
  public readonly pricesTableRowTemplatesTypedToken!: {
    rowData: CheckoutPriceItem;
  };

  public readonly countriesTypedToken!: Country;
  public readonly terminalsTypedToken!: Terminal;

  public readonly gendersOptions = GENDERS_DATA;

  public readonly hotelsTableColumns = CHECKOUT_HOTELS_TABLE_COLUMNS;
  public readonly hotelsData = computed<ICheckoutHotelData[] | null>(() => {
    const hotels = this.hotelsFormValue();
    if (!hotels?.length) return null;

    const touristsList = this.touristsInfo();
    if (!touristsList?.length) return null;

    return untracked(() => this.#mapHotelsData(hotels, touristsList));
  });

  public readonly transfersAndVisaDataTypedToken!: ICheckoutTransfersData;
  public readonly transfersAndVisaTableColumns = CHECKOUT_TRANSFERS_AND_VISAS_TABLE_COLUMNS;
  public readonly touristsWithTransfersData = computed<ICheckoutTransfersData[] | null>(() => {
    const touristsInfo = this.touristsInfo();
    if (!touristsInfo?.length) return null;
    return untracked(() => this.#mapTouristsWithTransfersData(touristsInfo));
  });

  // #region Transfer requirement (QA doc docs features/iway-transfers/qa.md R-HT10 .. R-HT13)

  /** The destination's Transfer requirement of this booking, from the transfer blocks the backend sent; null = no policy or no block. */
  public readonly transferRequirement = computed<'optional' | 'mandatory' | 'included' | null>(() => {
    for (const tourist of this.#checkoutFormOut()?.tourists ?? [])
      for (const transfer of tourist.transfers ?? []) if (transfer.requirement) return transfer.requirement;
    return this.aviaTransfer()?.requirement ?? null;
  });

  /** Avia only (qa.md R-AT1 .. R-AT8): the transfer step of a flights-only booking - requirement, the hotels to go to, the chosen one. */
  public readonly aviaTransfer = computed(() => this.#checkoutFormOut()?.aviaTransfer ?? null);

  /** Avia only (qa.md R-AT2): "Where to?" - the hotel the agent chose; sent with every calculation, verification and the confirm. */
  public readonly transferHotelCode = signal<string | null>(null);

  /** Avia only: the agent chose (or cleared) where the guests go - the transfer blocks of that hotel are calculated. */
  public chooseTransferHotel(hotelCode: string | null): void {
    if ((hotelCode ?? null) === this.transferHotelCode()) return;
    this.transferHotelCode.set(hotelCode ?? null);
    this.#verifyGeneration++;
    // a calculation with tourists refuses missing birth dates (found on dev 09.10.2026) - the cars are asked once every tourist has one
    this.#whenBirthDatesReady(() => this.#reCalcCheckout().subscribe());
  }

  /** Avia only: a hotel is chosen but the cars wait for the tourists' birth dates (the panel says so). */
  public readonly transferWaitsForBirthDates = computed<boolean>(
    () =>
      !!this.transferHotelCode() &&
      !this.hasTransferBlocks() &&
      !(this.touristsInfo() ?? []).every((t) => t.birthDate !== null && t.birthDate !== undefined)
  );

  /**
   * R-CK27 (packages-api#92): a manual Arrival / Departure time that is typed but not whole ("09:") must not go to the server in the background
   * price check (packages-api answered 500 for it). An empty time is fine - the agent may not have reached the block yet, the server takes it.
   */
  /** The echo of a stored manual flight time (`9:30`, `09:30:00`) in the shape the masked time box takes (`09:30`). */
  #withWholeFlightTimes(response: CheckoutFormOut): CheckoutFormOut {
    if (!response.tourists?.some((t) => !!t.arrivalFlightInfo?.time || !!t.departureFlightInfo?.time)) return response;
    const fix = <T extends { time?: string | null } | null | undefined>(info: T): T =>
      info ? { ...info, time: normalizeFlightTime(info.time) } : info;
    return {
      ...response,
      tourists: response.tourists.map((t) => ({
        ...t,
        arrivalFlightInfo: fix(t.arrivalFlightInfo),
        departureFlightInfo: fix(t.departureFlightInfo)
      }))
    };
  }

  #manualFlightTimesWhole(): boolean {
    return this.touristsFormArray.controls.every((t) =>
      [t.controls.arrivalFlightInfo.controls.time, t.controls.departureFlightInfo.controls.time].every(
        (time) => !time.value || time.valid
      )
    );
  }

  #birthDatesReady(): boolean {
    return this.touristsFormArray.controls.every(
      (t) => t.controls.birthDate.value !== null && t.controls.birthDate.valid
    );
  }

  /** Runs `then` now when every tourist has a valid birth date, else once they all have one. */
  #whenBirthDatesReady(then: () => void): void {
    if (this.#birthDatesReady()) {
      then();
      return;
    }
    this.touristsFormArray.valueChanges
      .pipe(
        takeWhile(() => !this.#destroyListeners()),
        filter(() => this.#birthDatesReady()),
        take(1)
      )
      .subscribe(() => then());
  }

  /** True when the backend built at least one transfer block: the destination offers a transfer for this sales mode. Off = the panel is hidden. */
  public readonly hasTransferBlocks = computed<boolean>(() =>
    (this.#checkoutFormOut()?.tourists ?? []).some((t) => (t.transfers?.length ?? 0) > 0)
  );

  /** The cars chosen so far, one entry per transfer block (the same car for every tourist of a block is listed once). */
  public readonly chosenTransfers = computed<ICheckoutChosenTransfer[]>(() => {
    const chosen = new Map<string, string | null | undefined>();
    for (const tourist of this.touristsInfo() ?? [])
      for (const transfer of tourist.transfers ?? [])
        chosen.set(`${tourist.id}|${transfer.id}`, transfer.selectedTransferPriceId);

    const byBlock = new Map<string, ICheckoutChosenTransfer>();
    for (const tourist of this.#checkoutFormOut()?.tourists ?? [])
      for (const transfer of tourist.transfers ?? []) {
        if (transfer.type !== CheckoutTransferTypeEnum.Arrival && transfer.type !== CheckoutTransferTypeEnum.Departure)
          continue;
        if (!transfer.id || byBlock.has(transfer.id)) continue;
        const key = `${tourist.id}|${transfer.id}`;
        // R-HT12: an included car is chosen by the server whatever the form holds (the agent has no selector), so the server's choice is shown
        const selected =
          transfer.requirement === 'included' || !chosen.has(key) ? transfer.selectedTransferPriceId : chosen.get(key);
        const car = transfer.transferPrices?.find((p) => p.id === selected);
        if (!car || /WITHOUT/i.test(car.name ?? '')) continue;
        byBlock.set(transfer.id, {
          id: transfer.id,
          direction: transfer.type === CheckoutTransferTypeEnum.Departure ? 'departure' : 'arrival',
          name: car.name ?? '',
          price: car.adultPrice ?? 0,
          isLive: !!car.isLive,
          supplierName: car.supplierName ?? null,
          // R-HT9a: a car with no adult and no child price (a tour package's per-person car is priced inside the package) reads "included";
          // only the label - `price` stays as it was, so every total is unchanged
          included: transfer.requirement === 'included' || (!car.adultPrice && !car.childPrice)
        });
      }
    return [...byBlock.values()];
  });

  /** C23: the trace id of the last calculation - shown next to a coded transfer reason so support can follow it. */
  public readonly calcTraceId = computed<string | null>(() => this.#checkoutFormOut()?.traceId ?? null);

  /** C17: the live transfer suppliers that did not answer - named on the page while the other suppliers' cars still show. */
  public readonly unavailableTransferSuppliers = computed<string[]>(
    () => this.#checkoutFormOut()?.unavailableTransferSuppliers ?? []
  );

  /**
   * C17 (R-HT10, website-flags R-WF111): a live transfer supplier did not answer - also when online-api dropped the names (an agent without
   * `website-details-tags`), then the panel says so without naming them.
   */
  public readonly transfersUnavailable = computed<boolean>(
    () =>
      this.#checkoutFormOut()?.dynamicTransfersUnavailable === true || this.unavailableTransferSuppliers().length > 0
  );

  /**
   * C3: why "Register the reservation" is disabled for the transfer - the destination requires one and none is chosen yet
   * (`CHECKOUT_TRANSFER_REQUIRED`), or nobody can price it for this airport and hotel (`CHECKOUT_TRANSFER_NOT_OFFERED`). The server
   * enforces the same at Confirm; this only says so before the click. Null = the transfer does not block.
   */
  public readonly transferBlockReason = computed<{ code: string; text: string } | null>(() => {
    const requirement = this.transferRequirement();
    if (requirement !== 'mandatory' && requirement !== 'included') return null;
    // R-AT4: avia only - without a hotel to go to there is no transfer block yet, the hotel itself is what is missing
    const avia = this.aviaTransfer();
    if (avia && !avia.hotelCode && !this.transferHotelCode())
      return {
        code: 'CHECKOUT_TRANSFER_HOTEL_REQUIRED',
        text: 'A transfer is required for this destination. Choose the hotel the guests go to and a car.'
      };
    const chosen = new Map<string, string | null | undefined>();
    for (const tourist of this.touristsInfo() ?? [])
      for (const transfer of tourist.transfers ?? [])
        chosen.set(`${tourist.id}|${transfer.id}`, transfer.selectedTransferPriceId);

    let required = false;
    for (const tourist of this.#checkoutFormOut()?.tourists ?? [])
      for (const transfer of tourist.transfers ?? []) {
        if (transfer.type !== CheckoutTransferTypeEnum.Arrival && transfer.type !== CheckoutTransferTypeEnum.Departure)
          continue;
        if (transfer.notOffered)
          return {
            code: 'CHECKOUT_TRANSFER_NOT_OFFERED',
            text: 'No supplier can price a transfer for this airport and hotel, so this offer cannot be booked. Choose another airport or hotel.'
          };
        const selected = chosen.has(`${tourist.id}|${transfer.id}`)
          ? chosen.get(`${tourist.id}|${transfer.id}`)
          : transfer.selectedTransferPriceId;
        const hasCars = !!transfer.transferPrices?.length;
        if (
          requirement === 'mandatory' &&
          (!hasCars || !selected || !transfer.transferPrices!.some((p) => p.id === selected))
        )
          required = true;
        if (requirement === 'included' && !hasCars) required = true;
      }
    return required
      ? {
          code: 'CHECKOUT_TRANSFER_REQUIRED',
          text: 'A transfer is required for this destination. Choose the airport and a car to continue.'
        }
      : null;
  });

  // #endregion

  public readonly touristsWithFlightsData = computed<ICheckoutFlightsData[] | null>(() => {
    const touristsList = this.touristsInfo();
    if (!touristsList?.length) return null;
    const touristsWithFlightData = touristsList.filter((t) => t.flights?.length);
    if (!touristsWithFlightData.length) return null;
    return untracked(() => this.#mapTouristsWithFlightsData(touristsWithFlightData));
  });
  public readonly flightsTableColumns = CHECKOUT_FLIGHTS_TABLE_COLUMNS;
  public readonly flightsTableRowTemplatesTypedToken!: {
    rowData: ICheckoutFlightsData;
  };

  public readonly touristsWithInsuranceData = computed<ITouristWithInsuranceData[]>(
    () => this.touristsInfo()?.filter((e) => e.insurancePrice) ?? []
  );
  public readonly insurancesTableColumns = CHECKOUT_INSURANCES_TABLE_COLUMNS;
  public readonly insurancesTableRowTemplatesTypedToken!: {
    rowData: ITouristWithInsuranceData;
  };

  /** TravelDesk Plan 2b: the excursion lines of the calc response, with the reason when one cannot be confirmed. */
  public readonly excursionsData = computed<CheckoutExcursionOut[]>(() => this.#checkoutFormOut()?.excursions ?? []);

  /** The paid extras of the package's live cars, one entry per car (docs features/iway-transfers/qa.md R-38, J-18). */
  public readonly transferExtrasData = computed<ICheckoutTransferExtras[]>(() => {
    const byTransfer = new Map<string, ICheckoutTransferExtras>();
    for (const tourist of this.#checkoutFormOut()?.tourists ?? [])
      for (const transfer of tourist.transfers ?? [])
        if (transfer.extras?.length && !byTransfer.has(transfer.id ?? ''))
          byTransfer.set(transfer.id ?? '', {
            id: transfer.id ?? '',
            direction: transfer.type === CheckoutTransferTypeEnum.Departure ? 'departure' : 'arrival',
            extras: transfer.extras
          });
    return [...byTransfer.values()];
  });

  public readonly copyManualFlightDataControl = new FormControl<boolean>(true);

  // #endregion

  // #region Form Group

  public readonly formGroup = new FormGroup(new CheckoutTypedForm());

  public readonly touristsInfo = toSignal(
    this.formGroup.controls.tourists.valueChanges.pipe(map((e) => e.map((t, _formIndex) => ({ ...t, _formIndex }))))
  );

  public readonly hotelsFormValue = toSignal(
    this.formGroup.controls.hotels.valueChanges.pipe(map((e) => e?.map((h, _formIndex) => ({ ...h, _formIndex }))))
  );

  // #endregion

  // #region Accessors

  public get countries() {
    return this.#selectors.countries;
  }

  public get terminals() {
    return this.#selectors.terminals;
  }

  /** R-HT24: the airports of the transfer step as the agent reads them - "Antalya Airport (AYT2)", every core terminal, none merged. */
  public readonly terminalOptions = computed(() => {
    const terminals = this.#selectors.terminals();
    return { ...terminals, response: terminalOptions(terminals.response) };
  });

  public get touristsFormArray() {
    return this.formGroup.controls.tourists;
  }

  public get priceCurrency() {
    return this.formGroup.controls.priceCurrency.value;
  }

  /** Current value of the "Copy information about flights for all tourists" box (starts ticked); one signal, kept in step with the box. */
  public readonly allowCopyManualFlightData = toSignal(
    this.copyManualFlightDataControl.valueChanges.pipe(startWith(this.copyManualFlightDataControl.value)),
    { requireSync: true }
  );

  public get formState() {
    return this.#selectors.calcCheckoutState;
  }

  public get verifyCalculatedPricesState() {
    return this.#selectors.verifyCalculatedPricesState;
  }

  // #endregion

  // #region Utils

  public scrollToFirstInvalidControlFn?: () => void;

  // #endregion

  // #region Public API

  /** Initializes the service and starts tracking cart item changes. */
  public init(): void {
    this.#destroyListeners.set(false);
    this.#trackCartItemsChanges();
  }

  /** Cleans up listeners and resets the form state. */
  public destroy(): void {
    this.#resetForm();
    this.#destroyListeners.set(true);
    this.isReady.set(false);
    // R-SS7: the acceptance is never carried over - a new checkout (or a reload) starts unticked; the service is route-scoped, so the
    // last CalcCheckout answer (its on-request warning) is dropped too - the next visit reads only its own answers
    this.onRequestAccepted.set(false);
    this.#onRequestRefusal.set(null);
    this.#checkoutFormOut.set(null);
    // ADLC-7 R-SR11: an unsaved special request is not kept after the checkout is left
    this.#specialRequests?.reset();
  }

  /** Copies manual flight data from the first tourist to all subsequent tourists. */
  public copyAllManualFlightData(): void {
    const arrivalFlightInfo = this.touristsFormArray.controls[0].controls.arrivalFlightInfo.getRawValue();
    const departureFlightInfo = this.touristsFormArray.controls[0].controls.departureFlightInfo.getRawValue();
    for (let index = 1; index < this.touristsFormArray.length; index++) {
      this.touristsFormArray.controls[index].controls.arrivalFlightInfo.setValue({
        ...arrivalFlightInfo,
        time: normalizeFlightTime(arrivalFlightInfo.time)
      });
      this.touristsFormArray.controls[index].controls.departureFlightInfo.setValue({
        ...departureFlightInfo,
        time: normalizeFlightTime(departureFlightInfo.time)
      });
    }
  }

  /** Copies a single manual flight data field from the first tourist to all subsequent tourists. */
  public onCopyManualFlightData(
    value: string | Date | null,
    field: keyof ManualFlightInfo,
    from: keyof Pick<CheckoutTouristIn, 'arrivalFlightInfo' | 'departureFlightInfo'>
  ): void {
    for (let index = 1; index < this.touristsFormArray.length; index++) {
      this.touristsFormArray.controls[index].controls[from].controls[field].setValue(
        (field === 'time' ? normalizeFlightTime(value) : value) as never
      );
    }
  }

  /** Opens the tourist check-in configuration modal for a specific hotel. */
  public openTouristCheckInConfig(hotel: PackageHotel, index: number): void {
    if (!hotel.hotelPriceId) return;

    const hotelFormGroup = this.formGroup.controls.hotels.controls;

    if (!hotelFormGroup) return;
    const idx = hotelFormGroup.length > 1 ? index : 0;
    const hotelFormData = hotelFormGroup[idx].getRawValue();
    const tourists = this.formGroup.controls.tourists.getRawValue() as CheckoutTouristIn[];

    const data: CheckoutTouristCheckInConfigParams = {
      hotel,
      hotelFormData,
      tourists
    };

    const modal = this.#dynamicModalService.open({
      title: 'Check in tourists',
      component: CheckoutTouristCheckInConfigFormComponent,
      data
    });
    modal.closed.pipe(take(1)).subscribe((result: ICheckoutHotelFormValues) => {
      if (result) {
        hotelFormGroup[idx].patchValue(result);
        this.#reCalcCheckout().subscribe();
      }
    });
  }

  /** Opens the transfer and visa form modal for a specific tourist. */
  public openTransferAndVisaForm(index: number): void {
    const touristForm = this.touristsFormArray.controls[index];
    if (!touristForm?.value) return;

    this.#reCalcCheckout({ selectedTouristId: touristForm.value.id }).subscribe(({ loadState }) => {
      if (loadState.status !== 'completed') return;

      const data: CheckoutTransferAndVisaParams = {
        tourist: touristForm.value as CheckoutTouristIn,
        index,
        canCopy: this.touristsFormArray.length > 1
      };

      const modal = this.#dynamicModalService.open({
        title: 'Transfers',
        component: CheckoutTransferAndVisaFormComponent,
        data
      });
      modal.closed.pipe(take(1)).subscribe((result?: CheckoutTransferAndVisaSaveData) => {
        if (!result) return;
        if (!touristForm) return;
        touristForm.controls.selectedVisaId.setValue(result.selectedVisaId);
        touristForm.controls.transfers.patchValue(result.transfers);
        if (result.copy) {
          this.formGroup.controls.tourists.controls.forEach((e) => {
            if (e.controls.id.value !== touristForm.controls.id.value) {
              e.controls.selectedVisaId.setValue(result.selectedVisaId);
              e.controls.transfers.patchValue(result.transfers);
            }
          });
        }
        // R-HT15: the price lines follow the chosen car at once - the displayed total is what Register will store; a verification asked for
        // before the choice is stale from here on
        this.#verifyGeneration++;
        this.#reCalcCheckout().subscribe();
      });
    });
  }

  /** Handles backend validation errors and sets form-level error states. */
  public handleFormErrors(response: CheckoutFormOut): void {
    if (!response) return;
    if (response.containsError) {
      response.hotels?.forEach((hotel) => {
        this.hotelErrors.update((data) => {
          data[String(hotel.hotelPriceId)] = hotel?.errorMessage ?? '';
          return data;
        });
      });
      console.error('checkout error', response);

      response.tourists?.forEach((tourist, index) => {
        if (tourist.errorCode === 'INVALID_BIRTH_DATE' && tourist.errorMessage) {
          this.formGroup.controls.tourists.controls[index].controls.birthDate.setErrors({
            errorMessage: tourist.errorMessage
          });
        } else if (tourist.errorCode === 'PASSPORT_NUMBER_IS_INVALID' && tourist.errorMessage)
          this.formGroup.controls.tourists.controls[index].controls.passportNumber.setErrors({
            errorMessage: tourist.errorMessage
          });
      });
      this.formGroup.markAllAsTouched();

      // website-web#13 / R-2: "fill in the fields" only when a tourist / hotel line really carries an error; any other failure shows
      // the server's own message and its trace id (C23), so the agent does not re-check a correct form and support gets an id.
      const hasFieldError =
        !!response.tourists?.some((t) => [t.errorCode, t.errorMessage].some(Boolean)) ||
        !!response.hotels?.some((h) => [h.errorCode, h.errorMessage].some(Boolean));
      // ADLC-1 R-SS5 / AC-47: a refused registration of a show-only tour (FLIGHT_PRICE_OVERBOOKED) - the site's own EN / RU text, Trace ID
      // ADLC-1 R-SS7a: the on-request warning was not accepted - nothing registered; untick, back to the box, the Trace ID
      if (!hasFieldError && !response.reservationId && isOnRequestNotAccepted(response)) {
        this.#showOnRequestNotAccepted(response, response.traceId ?? response.activityId);
        return;
      }
      const noSeats = !hasFieldError ? this.#noSeatsRefusalText(response) : null;
      if (noSeats) {
        this.#showNoSeatsRefusal(noSeats, response.traceId ?? response.activityId);
        return;
      }
      if (!hasFieldError) {
        this.#confirmService.confirm(
          [response.userErrorDescription, response.errorDescription].find(Boolean) ??
            'The request could not be completed. Please try again; if it keeps failing, quote the Trace ID.',
          { type: 'error', activityId: response.traceId ?? response.activityId ?? undefined }
        );
        return;
      }

      this.#confirmService
        .confirm('Please fill in all required fields correctly', 'error')
        .subscribe(() => this.scrollToFirstInvalidControlFn && this.scrollToFirstInvalidControlFn());
    }
  }

  /** R-8 / R-10: the agent keeps the booking; it is registered on request (not confirmed at once). */
  public instantContinueOnRequestFor(hotelPriceId: string): void {
    this.instantContinueOnRequest.update((continued) => ({ ...continued, [hotelPriceId]: true }));
  }

  /**
   * R-8: the agent goes back to search for another price - the cart item holding this hotel is dropped. With nothing left in the
   * cart the checkout already leaves for the previous page (the search, #trackCartItemsChanges); otherwise we go back from here.
   */
  public instantBackToSearch(hotelPriceId: string): void {
    const items = this.#selectors.cartItems();
    const holding = items.filter((item) => 'hotel' in item.data && item.data.hotel?.hotelPriceId === hotelPriceId);
    holding.forEach((item) => this.#storeActions.cartRemoveItem(item.id));
    if (holding.length === 0 || items.length > holding.length) this.#storeActions.goPreviousPage();
  }

  /** Validates the form and submits the checkout data. */
  public onSubmit(): void {
    if (this.instantRegisterBlocked()) return; // R-8: the button is disabled with the reason; a stray submit changes nothing
    if (this.transferBlockReason()) return; // R-HT11 / R-HT13: the same - the server refuses it too (CHECKOUT_TRANSFER_REQUIRED / _NOT_OFFERED)
    // ADLC-1 R-SS7: Register is disabled with the reason until the on-request box is ticked; a stray submit goes to the box, sends nothing
    if (this.onRequestRegisterBlocked()) {
      this.onRequestFocusFn?.();
      return;
    }
    this.#updateValidators();
    this.formGroup.updateValueAndValidity();
    if (Object.keys(this.hotelErrors).length) this.hotelErrors.set({});

    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.#confirmService
        .confirm('Please fill in all required fields correctly', 'error')
        .subscribe(() => this.scrollToFirstInvalidControlFn && this.scrollToFirstInvalidControlFn());
      return;
    }

    const { hotels, tourists, note, contactPhone, reservationId } = this.formGroup.getRawValue();
    const expectedFlightTotals = this.#expectedFlightTotals();
    // docs#451 P5 (R-EUI41): each excursion's total as the last CalcCheckout / verification answered it - sent on Confirm only
    const expectedExcursionTotals = this.#excursionTotalsSeen();

    this.#setRegistering(true);
    this.#storeActions.confirmCheckoutFormData({
      hotels: this.#withSpecialRequests(this.#withExtensionChoice(this.#withInstantChoice(hotels))),
      tourists,
      note,
      contactPhone,
      reservationId,
      seatHoldToken: this.seatHoldToken(),
      transferHotelCode: this.transferHotelCode(),
      // ADLC-1 R-SS7: top level, only with the step on AND ticked - never sent without the agent's tick (packages-api reads it top level)
      ...(this.onRequestStep() && this.onRequestAccepted() ? { acceptSeatsOnRequest: true } : {}),
      // R-FH16 / R-TB16: only when the backend told us the per-flight totals (avia flights and the flight part of tours) - an older backend gets today's request
      ...(Object.keys(expectedFlightTotals).length ? { expectedFlightTotals } : {}),
      ...(Object.keys(expectedExcursionTotals).length ? { expectedExcursionTotals } : {})
    } as CalcCheckoutPayload);

    this.#actions
      .pipe(
        ofActionCompleted(confirmCheckoutFormData),
        takeWhile(() => !this.#destroyListeners())
      )
      .pipe(
        switchMap(() =>
          this.#store.select(CheckoutState.confirmCheckoutResult).pipe(
            filter((e) => e.loadState.status !== 'loading'),
            take(1),
            tap((result) => {
              if (!result?.response || result.loadState.status === 'error') {
                throw result.loadState;
              }
              if (!result?.response) return;
              // ADLC-1 AC-47: a 200 `error: true` FLIGHT_PRICE_OVERBOOKED without containsError - the same text and Trace ID, nothing registered
              const noSeats =
                result.response.containsError || isRegisteredAnswer(result.response)
                  ? null
                  : this.#noSeatsRefusalText(result.response);
              const extensionRefusal = this.#extensionRefusal(result.response);
              // ADLC-1 R-SS7a: a 200 FLIGHT_SEATS_ON_REQUEST_NOT_ACCEPTED (error: true, no containsError) - nothing registered
              const notAccepted =
                !result.response.containsError &&
                !result.response.reservationId &&
                isOnRequestNotAccepted(result.response);
              // R-FH16: the airline's price went up between the checkout and the confirm - nothing is registered, the agent decides
              if (this.#isFlightPriceChanged(result.response)) this.#askFlightPriceChanged(result.response);
              // ECI/LCO V1: the extra night's price moved or it is gone - nothing is registered, the agent decides
              else if (extensionRefusal)
                this.#askExtensionChange(extensionRefusal, result.response.traceId ?? result.response.activityId);
              else if (result?.response.containsError) this.handleFormErrors(result?.response);
              else if (notAccepted)
                this.#showOnRequestNotAccepted(result.response, result.response.traceId ?? result.response.activityId);
              else if (noSeats)
                this.#showNoSeatsRefusal(noSeats, result.response.traceId ?? result.response.activityId);
              else {
                this.#applyInstantState(result.response);
                // R-8: instant confirmation was lost at the very last moment - nothing is registered, the agent chooses first
                if (!result.response.reservationId && this.instantRegisterBlocked()) return;
                void this.#afterConfirmed(result.response);
              }
            }),
            catchError((error: DataLoadStatusModel) => {
              const errorResponse = error.errorResponse as ApiErrorResponse<CheckoutFormOut>;
              if (errorResponse?.error && this.#isFlightPriceChanged(errorResponse.error)) {
                // R-FH16: a business answer the agent now decides on - handled, not an application error
                this.#askFlightPriceChanged(errorResponse.error, errorResponse.activityId);
                return of(undefined);
              }
              const extensionRefusal = this.#extensionRefusal(errorResponse?.error);
              if (extensionRefusal) {
                // ECI/LCO V1: the same - a business answer the agent decides on
                this.#askExtensionChange(extensionRefusal, errorResponse.error?.traceId ?? errorResponse.activityId);
                return of(undefined);
              }
              // ADLC-1 R-SS7a: FLIGHT_SEATS_ON_REQUEST_NOT_ACCEPTED as an error body - a business answer, handled (nothing registered)
              if (
                !errorResponse?.error?.containsError &&
                !errorResponse?.error?.reservationId &&
                isOnRequestNotAccepted(errorResponse?.error)
              ) {
                this.#showOnRequestNotAccepted(
                  errorResponse.error,
                  errorResponse.error?.traceId ?? errorResponse.error?.activityId ?? errorResponse.activityId
                );
                return of(undefined);
              }
              // ADLC-1 AC-47: FLIGHT_PRICE_OVERBOOKED as an error body (no containsError) - the same text and Trace ID as the 200 answer
              const noSeats = errorResponse?.error?.containsError
                ? null
                : this.#noSeatsRefusalText(errorResponse?.error);
              if (noSeats) {
                // a business answer the agent now acts on (Other flights) - handled, not an application error (as R-FH16)
                this.#showNoSeatsRefusal(
                  noSeats,
                  errorResponse.error?.traceId ?? errorResponse.error?.activityId ?? errorResponse.activityId
                );
                return of(undefined);
              }
              if (errorResponse?.error?.containsError) this.handleFormErrors(errorResponse.error);
              else {
                this.#confirmService.confirm(error.errorMessage ?? '', {
                  type: 'error',
                  activityId: errorResponse.activityId
                });
              }
              return throwError(() => error);
            })
          )
        ),
        take(1),
        finalize(() => this.#setRegistering(false))
      )
      .subscribe();
  }

  /** F7: Register the reservation is on its way (dispatch .. answer) - the special request lines are disabled, no popup opens. */
  #setRegistering(on: boolean): void {
    this.#specialRequests?.busy.set(on);
    this.specialRequestsSaving.set(!!this.#specialRequests?.saving());
  }

  /**
   * ADLC-7 R-SR9 / R-SR11 (contract s2 §2): each hotel line carries the `specialRequests` of its room, by the form index (the array of
   * the form's raw value, so index i = `_formIndex` i); a room with nothing set sends nothing. Never touches a price field.
   */
  #withSpecialRequests<T extends object>(hotels: T[]): (T & { specialRequests?: CheckoutHotelSpecialRequestsIn })[] {
    const requests = this.#specialRequests;
    if (!requests) return hotels;
    const rooms = this.hotelsData() ?? [];
    return hotels.map((hotel, i) => {
      const specialRequests = requests.toCheckoutIn(i, rooms);
      return specialRequests ? { ...hotel, specialRequests } : hotel;
    });
  }

  public onCitizenshipChanged(): void {
    this.#updateValidators();
  }

  /** R-FH16: a confirm refused because a flight's price went up (before anything was registered). */
  #isFlightPriceChanged(response: CheckoutFormOut | null | undefined): boolean {
    return response?.errorCode === FLIGHT_PRICE_CHANGED && !!response.flightPriceChange;
  }

  /**
   * R-FH16: "The price has changed" - Accept re-submits the confirm with the new total as the expected one (a second rise asks
   * again); Choose another fare takes the flight out of the cart and back to its fares; Cancel stays here, nothing is booked.
   */
  #askFlightPriceChanged(response: CheckoutFormOut, activityId?: string | null): void {
    const change = response.flightPriceChange!;
    const data: AviaPriceChangedDialogData = {
      change,
      code: response.errorCode,
      // section 11.9: online-api keeps traceId / errorCode / flightPriceChange intact (errorDescription is replaced by its generic text)
      activityId: response.traceId ?? response.activityId ?? activityId
    };
    const ref = this.#dynamicModalService.open({
      component: AviaPriceChangedDialogComponent,
      data,
      width: '520px',
      maxWidth: '94vw',
      // the dialog is as tall as its content (the site's modal default is a full-height panel)
      height: 'auto',
      mode: 'box',
      disableClose: true,
      dialogConfig: { ariaLabelledBy: 'avia-price-changed-title' }
    });
    ref.closed.pipe(take(1)).subscribe((choice?: AviaPriceChangedChoice) => {
      if (choice === 'accept') {
        if (change.packageId && typeof change.newTotalPrice === 'number')
          this.#aviaTotalsAccepted.update((accepted) => ({ ...accepted, [change.packageId!]: change.newTotalPrice! }));
        this.onSubmit();
      } else if (choice === 'other') this.#chooseAnotherFare(change.packageId);
    });
  }

  /**
   * "Choose another fare": the cart item of that flight goes, and its row opens its details again on the results (R-FH16). A TOUR whose flight part
   * rose (R-TB16) goes the same way: the tour leaves the cart and the Tours row of the same hotel offer + itinerary opens its Details on its fares.
   */
  #chooseAnotherFare(packageId: string | null | undefined): void {
    const items = this.#selectors.cartItems();
    const holding = items.filter(
      (item) =>
        (item.type === 'avia' &&
          (item.data.id === packageId || !!item.data.fares?.some((fare) => fare.packageId === packageId))) ||
        (item.type === 'tour' && item.data.id === packageId)
    );
    const flight = holding[0];
    if (flight?.type === 'tour') this.#tourFareReturn.requestReopen(flight.data);
    // R-FP35: the details reopen on the fare the cart held (FLEX), not on the cheapest
    if (flight?.type === 'avia')
      this.#fareReturn.requestReopen(aviaFlightKey(flight.data), {
        packageId: flight.data.id,
        code: flight.selectedFareCode ?? null
      });
    holding.forEach((item) => this.#storeActions.cartRemoveItem(item.id));
    if (holding.length === 0 || items.length > holding.length) this.#storeActions.goPreviousPage();
  }

  /** R-FH19: a registered reservation whose flight hold the airline refused - the agent is told first, then the usual result. */
  async #afterConfirmed(response: CheckoutFormOut): Promise<void> {
    if (response.flightHold) {
      const data: AviaHoldRefusedNoticeData = {
        hold: response.flightHold,
        rfNumber: response.rfNumber,
        activityId: response.traceId ?? response.activityId
      };
      const ref = this.#dynamicModalService.open({
        component: AviaHoldRefusedNoticeComponent,
        data,
        width: '520px',
        maxWidth: '94vw',
        height: 'auto',
        mode: 'box',
        dialogConfig: { ariaLabelledBy: 'avia-hold-refused-title' }
      });
      await new Promise<void>((resolve) => ref.closed.pipe(take(1)).subscribe(() => resolve()));
    }
    // ADLC-7 R-SR12 / R-SR19g: registered, but the special requests were not saved - the agent is told (code + Trace ID) before the
    // reservation page opens, where they can be added again
    if (response.specialRequests?.status === 'notSaved' && response.reservationId) {
      const data: SpecialRequestsNotSavedData = {
        errorCode: response.specialRequests.errorCode,
        traceId: response.specialRequests.traceId ?? response.traceId ?? response.activityId
      };
      const ref = this.#dynamicModalService.open({
        component: SpecialRequestsNotSavedDialogComponent,
        data,
        width: '520px',
        maxWidth: '94vw',
        height: 'auto',
        mode: 'box',
        dialogConfig: { ariaLabelledBy: 'sr-ns-message' }
      });
      await new Promise<void>((resolve) => ref.closed.pipe(take(1)).subscribe(() => resolve()));
    }
    await this.#handleRefreshPriceResult(response, true, false);
  }

  /**
   * docs#451 P5 (R-EUI41): each excursion's total in its PACKAGE currency as the last CalcCheckout / verification answered it -
   * payableTotal + agentCommissionAmount, which is the server's TotalPrice (never the header-converted figure the agent reads, so
   * packages-api compares like with like). A verification that re-priced it replaces it, so after "The price has changed" the total
   * the agent accepted is the one Confirm expects.
   */
  readonly #excursionTotalsSeen = signal<Record<string, number>>({});

  /**
   * The per-flight totals of a CalcCheckout / verification answer (R-FH16); an answer without them leaves the last ones.
   * The same answer sets the excursion totals (R-EUI41), replaced as a whole by every answer that has excursion lines.
   */
  #rememberAviaTotals(response: CheckoutFormOut | null | undefined): void {
    if (response?.excursions) {
      const excursions: Record<string, number> = {};
      for (const line of response.excursions)
        if (line.packageId && typeof line.payableTotal === 'number')
          // the sum is the server's TotalPrice exactly (Q-3 rounding included); 2 decimals only drop the float noise
          excursions[line.packageId] = Math.round((line.payableTotal + (line.agentCommissionAmount ?? 0)) * 100) / 100;
      this.#excursionTotalsSeen.set(excursions);
    }
    if (!response?.flightTotals?.length) return;
    const seen: Record<string, number> = {};
    for (const total of response.flightTotals)
      if (total.packageId && typeof total.totalPrice === 'number') seen[total.packageId] = total.totalPrice;
    this.#aviaTotalsSeen.set(seen);
    this.#aviaTotalsAccepted.set({});
  }

  // #endregion

  // #region Data Loading

  #trackCartItemsChanges(): void {
    const effectRef = effect(
      () => {
        const itemsCount = this.#selectors.itemsCount();

        if (this.#destroyListeners()) {
          effectRef.destroy();
          return;
        }
        untracked(() => {
          this.isReady.set(false);
          if (itemsCount > 0) {
            this.#loadCountries();
            this.#resetForm();
            this.#calcCheckout();
          } else {
            this.#resetStateAndDestroy();
            this.#storeActions.goPreviousPage();
          }
        });
      },
      { injector: this.#injector }
    );
  }

  #loadCountries(): void {
    this.#storeActions.getCountries();
  }

  #calcCheckout(payload?: CalcCheckoutPayload) {
    const isInitialRequest = !payload;

    this.#storeActions.calcCheckout(payload).pipe(takeWhile(() => !this.#destroyListeners()));
    const subscription = this.#actions.pipe(ofActionCompleted(calcCheckout)).pipe(
      switchMap(() =>
        this.#store.select(CheckoutState.calcCheckoutResult).pipe(
          filter((e) => e.loadState.status !== 'loading'),
          tap((result) => {
            if (!result?.response || result.loadState.status === 'error') {
              throw result.loadState;
            }

            // ADLC-1 R-SS5 / AC-45: FlightPriceNoMoreAvailable + NO_SEATS names the short flight and the party (EN / RU) with the Trace ID
            const noSeats = this.#noSeatsRefusalText(result.response);
            const priceUnavailableMessage =
              noSeats ??
              this.#commonHelpersService.getPriceUnavailableMessage(
                result.response.refreshPriceResult,
                result.response.excursions
              );
            // A stop-sale item can also come back as a "successful" calculation that simply has
            // nothing left in it - treat that the same way instead of rendering an empty form.
            if (priceUnavailableMessage || (isInitialRequest && !this.#hasBookableContent(result.response))) {
              this.#leaveOnPriceUnavailable(
                priceUnavailableMessage,
                true,
                noSeats ? (result.response.traceId ?? result.response.activityId) : null
              );
              return;
            }

            this.#checkoutFormOut.set(result.response);
            this.#applyInstantState(result.response);
            this.#applyExtensionState(result.response);
            this.#rememberAviaTotals(result.response);
            if (isInitialRequest) this.#addFormArraysBasedOnData();
            else this.#syncTransferControls(result.response);
            this.formGroup.patchValue(this.#withWholeFlightTimes(result.response) as ICheckoutFormValues);
            if (isInitialRequest) {
              this.#updateValidators();
            }
            if (!this.isReady()) {
              this.isReady.set(true);
              this.#checkPricesOnBirthDateUpdate();
              // the cart's airport first: it recalculates on its own, so the terminal watch starts from it and adds no calculation (R-HT19)
              this.#applyCartTransferChoice();
              this.#recalcOnTerminalChange();
            }
          })
        )
      ),
      takeWhile(() => !this.#destroyListeners()),
      take(1),
      catchError((error: DataLoadStatusModel) => {
        this.#confirmService
          .confirm(error.errorMessage ?? '', {
            type: 'error',
            activityId: error.errorResponse?.activityId
          })
          .subscribe(() => (isInitialRequest ? this.#storeActions.goPreviousPage() : null));
        return of();
      })
    );
    if (isInitialRequest) subscription.subscribe();
    return subscription;
  }

  /**
   * R-HT12 / R-HT15: an included transfer is priced the moment the agent chooses the airport (the server picks the cheapest car), so the
   * price lines are recalculated at once - before, only the birth-date verification saw the new total and opened "The price has increased
   * by 72.75 $" for the transfer the destination includes (found on the live dev check 08.10.2026, UAE).
   * R-HT1 / R-HT12a (website-web#143): optional / mandatory - an airport change alone does not recalculate (the price changes only with a
   * chosen car), UNLESS the tourist holds a selection on a block of the changed direction - a car or the "WITHOUT" placeholder: Confirm
   * checks every row with a selection by its block id (packages-api CheckTransferSelection). The block id carries the terminal (`MLE-H` /
   * `H-MLE`), so the new terminal is a new block; only a full recalculation copies the answer's new ids into the form. Without it Register
   * sent the selection on the old `-H` / `H-` id with terminal MLE and was refused with CHECKOUT_TRANSFER_STALE.
   * A calculation with tourists refuses missing birth dates (R-HT19, 09.10.2026), so the recalculation waits for them - deferred, never
   * dropped, or the old ids would stay. The airport the cart's choice sets is the starting point (that path recalculates itself).
   */
  #recalcOnTerminalChange() {
    const legs = (): string[] =>
      this.touristsFormArray.controls.flatMap((t) => [
        `${t.controls.arrivalFlightInfo.controls.terminalCode.value ?? ''}`,
        `${t.controls.departureFlightInfo.controls.terminalCode.value ?? ''}`
      ]);
    let seen = legs();
    let waiting = false;
    this.touristsFormArray.valueChanges
      .pipe(
        takeWhile(() => !this.#destroyListeners()),
        map(() => legs().join('|')),
        startWith(seen.join('|')),
        distinctUntilChanged(),
        skip(1),
        debounceTime(300)
      )
      .subscribe(() => {
        const now = legs();
        const changed = now.flatMap((terminal, i) =>
          terminal === seen[i] ? [] : [{ tourist: Math.floor(i / 2), leg: i % 2 ? 'departure' : 'arrival' } as const]
        );
        seen = now;
        const recalc =
          this.transferRequirement() === 'included' ||
          changed.some(({ tourist, leg }) => this.#hasSelection(tourist, leg));
        if (!recalc || waiting) return;
        waiting = true;
        this.#whenBirthDatesReady(() => {
          waiting = false;
          this.#verifyGeneration++;
          this.#reCalcCheckout().subscribe();
        });
      });
  }

  /**
   * The tourist's form holds a selection on a block of that direction: any id of the block's prices, the "WITHOUT" placeholder included -
   * packages-api CheckTransferSelection checks every row with a non-empty selection by its block id (TransferSelectionRules: STALE for a
   * block not offered). No selection (null / empty) = nothing Confirm checks, so no recalculation (R-HT12a).
   */
  #hasSelection(touristIndex: number, leg: 'arrival' | 'departure'): boolean {
    const form = this.touristsFormArray.controls[touristIndex];
    if (!form) return false;
    const tourists = this.#checkoutFormOut()?.tourists ?? [];
    const blocks = (tourists.find((t) => t.id === form.controls.id.value) ?? tourists[touristIndex])?.transfers ?? [];
    return form.controls.transfers.getRawValue().some((row, k) => {
      // a row the form has not got an id for yet sits at its block's position (#syncTransferControls / #addFormArraysBasedOnData keep the order)
      const block = blocks.find((b) => !!row.id && b.id === row.id) ?? blocks[k];
      if (!row.selectedTransferPriceId || legOf(block) !== leg) return false;
      return (block?.transferPrices ?? []).some((price) => price.id === row.selectedTransferPriceId);
    });
  }

  /**
   * R-HT19: the hotel Details window let the agent choose the airport and the cars; the checkout opens with that choice - every tourist gets the
   * airport, then each block takes the same car (same id, else the same source and name: a live car's id may change) and the prices follow.
   * A car that is not offered any more is left unchosen (the requirement's reason then says what is missing - never a silent other car).
   * The airport is set at once; the cars are asked once every tourist has a valid birth date (a calculation with tourists refuses missing
   * birth dates - found on the live dev check 09.10.2026: CHECKOUT_INVALID_BIRTH_DATE right after the checkout opened).
   */
  #applyCartTransferChoice(): void {
    const item = this.#selectors.cartItems().find((i) => i.type === 'hotel' && !!i.transfer);
    const choice = item?.type === 'hotel' ? item.transfer : null;
    if (!choice || !(this.terminals().response ?? []).some((t) => t.terminalCode === choice.terminalCode)) return;

    for (const tourist of this.touristsFormArray.controls) {
      tourist.controls.arrivalFlightInfo.controls.terminalCode.setValue(choice.terminalCode as never); // the typed form says string[], the selector writes one code
      tourist.controls.departureFlightInfo.controls.terminalCode.setValue(choice.terminalCode as never);
    }
    const birthDatesReady = (): boolean =>
      this.touristsFormArray.controls.every((t) => t.controls.birthDate.value !== null && t.controls.birthDate.valid);
    this.touristsFormArray.valueChanges
      .pipe(
        startWith(null),
        takeWhile(() => !this.#destroyListeners()),
        filter(() => birthDatesReady()),
        take(1),
        switchMap(() => this.#reCalcCheckout()),
        filter(({ loadState }) => loadState.status === 'completed'),
        take(1)
      )
      .subscribe(() => this.#selectCartTransferCars(choice));
  }

  #selectCartTransferCars(choice: ICartHotelTransfer): void {
    (this.#checkoutFormOut()?.tourists ?? []).forEach((tourist, index) => {
      const form = this.touristsFormArray.controls[index];
      if (!form) return;
      const rows = (tourist.transfers ?? []).map((block) => {
        const leg = legOf(block);
        const arrival = leg === 'arrival';
        const id = arrival ? choice.arrivalCarId : choice.departureCarId;
        const seen = arrival ? choice.arrivalCar : choice.departureCar;
        const car = block.requirement === 'included' || !leg ? null : sameCar(carsOf(block), id, seen);
        return { id: block.id ?? null, selectedTransferPriceId: car?.id ?? block.selectedTransferPriceId ?? null };
      });
      while (form.controls.transfers.length < rows.length)
        form.controls.transfers.push(new FormGroup(new CheckoutTouristTransferTypedForm()));
      form.controls.transfers.patchValue(rows);
    });
    this.#verifyGeneration++;
    this.#reCalcCheckout().subscribe();
  }

  #checkPricesOnBirthDateUpdate() {
    // No `take(1)`: a birth date the user corrects after a failed verification (or changes
    // again afterwards) must trigger a fresh check every time, not just the first time all
    // birth dates happen to be filled in (CHECKOUT-3).
    this.touristsFormArray.valueChanges
      .pipe(
        takeWhile(() => !this.#destroyListeners()),
        debounceTime(1000),
        filter(
          (e) =>
            e.every((t) => t.birthDate !== null) &&
            this.touristsFormArray.controls.every((t) => t.controls.birthDate.valid) &&
            this.#manualFlightTimesWhole()
        )
      )
      .subscribe(() => {
        this.#verifyPrices();
      });
  }
  /**
   * The newest verification wins: an answer that was asked for before the agent changed the transfer is stale and must not open a "price
   * changed" dialog against the newer total (found on the live dev check 08.10.2026: a verification started by the birth dates answered after
   * the transfer dialog had been saved and read "The price has reduced by 47.97 $").
   */
  #verifyGeneration = 0;

  #verifyPrices(): void {
    const generation = ++this.#verifyGeneration;
    const { hotels, tourists, reservationId } = this.formGroup.getRawValue();

    this.#storeActions.verifyCalculatedPrices({
      hotels: this.#withExtensionChoice(hotels),
      tourists,
      reservationId,
      transferHotelCode: this.transferHotelCode()
    } as CalcCheckoutPayload);
    this.#actions
      .pipe(
        ofActionCompleted(checkoutVerifyCalculatedPrices),
        takeWhile(() => !this.#destroyListeners())
      )
      .pipe(
        switchMap(() =>
          this.#store.select(CheckoutState.verifyCalculatedPricesResult).pipe(
            takeWhile(() => !this.#destroyListeners()),
            filter((e) => e.loadState.status !== 'loading'),
            take(1),
            tap((data) => {
              if (!data.response || generation !== this.#verifyGeneration) return;
              // The backend answers a failed verification (e.g. CHECKOUT_INVALID_BIRTH_DATE) as a
              // normal completed response, not an HTTP error - `#handleRefreshPriceResult` only
              // reacts to `refreshPriceResult` and silently drops it (CHECKOUT-3). Surface it and
              // keep Register disabled instead of pretending the check never ran (C17).
              if (data.response.error || data.response.containsError) {
                // CHECKOUT-4 (05.10.2026): the tourist's own code (e.g. PASSPORT_ISSUE_DATE_REQUIRED) names the tourist and the field in the
                // site's language and marks the field; the line carries the Trace ID (C21, C23) - before, a generic English line without one.
                // docs#314 (19.10.2026): this check runs while the agent types, so it opens NO dialog (a modal at every pause named fields the
                // agent had not reached yet); the line under the form + the disabled Register are the reason (C3, R-6 / R-CK12).
                const message = this.#verificationMessage(data.response);
                this.priceVerificationError.set(message);
                this.priceVerificationTraceId.set(data.response.activityId ?? null);
                return;
              }
              this.priceVerificationError.set('');
              this.priceVerificationTraceId.set(null);
              this.#applyInstantState(data.response);
              this.#applyExtensionState(data.response);
              this.#rememberAviaTotals(data.response);
              this.#handleRefreshPriceResult(data.response, false, false);
            })
          )
        ),
        take(1)
      )
      .subscribe();
  }

  /** CHECKOUT-4: the server's own text first; else one line per tourist / hotel that carries a code, in the site's language; else the general line. */
  #verificationMessage(response: CheckoutFormOut): string {
    // R-LX7: an extras refusal reads the website's own text in the site language (the server's sentence is English with supplier codes)
    const extrasRefusal = extraServiceErrorText(response.errorCode);
    if (extrasRefusal) return this.#translate.instant(extrasRefusal);
    const own = response.userErrorDescription ?? response.errorDescription;
    if (own) return escapeHtml(own);
    const lines: string[] = [];
    (response.tourists ?? []).forEach((t, i) => {
      if (!t.errorCode && !t.errorMessage) return;
      const n = String(i + 1);
      const control = this.formGroup.controls.tourists.controls[i]?.controls;
      if (t.errorCode === 'PASSPORT_ISSUE_DATE_REQUIRED') control?.passportIssue.setErrors({ required: true });
      if (t.errorCode === 'PASSPORT_ISSUE_DATE_IS_INCORRECT')
        control?.passportIssue.setErrors({ errorMessage: t.errorMessage ?? t.errorCode });
      control?.passportIssue.markAsTouched();
      // website-web#83 (R-CK12): an age refusal of the price check marks Birthdate red, as Register does (R-CK11) - before only the line named the tourist
      if (t.errorCode === 'INVALID_BIRTH_DATE' || t.errorCode === 'BIRTH_DATE_IS_INCORRECT') {
        control?.birthDate.setErrors({ errorMessage: t.errorMessage ?? t.errorCode });
        control?.birthDate.markAsTouched();
      }
      const key = t.errorCode ? TOURIST_CHECK_TEXTS[t.errorCode] : undefined;
      // website-web#107 (R-CK25): a server text that is an English key (INVALID_BIRTH_DATE hotel / tour) reads in the site language
      // (an empty text is not translated - instant('') throws)
      const serverText = t.errorMessage ?? t.errorCode ?? '';
      const serverLine = !key && serverText ? escapeHtml(this.#translate.instant(serverText)) : '';
      lines.push(
        key ? this.#translate.instant(key, { n }) : `${this.#translate.instant('Tourist {n}', { n })}: ${serverLine}`
      );
    });
    (response.hotels ?? []).forEach((h) => {
      const hotelExtrasRefusal = extraServiceErrorText(h.errorCode);
      if (hotelExtrasRefusal) lines.push(this.#translate.instant(hotelExtrasRefusal));
      else if (h.errorMessage || h.errorCode) lines.push(escapeHtml(h.errorMessage ?? h.errorCode ?? ''));
    });
    return lines.length
      ? lines.join('<br>')
      : this.#translate.instant('The price could not be verified. Please review the tourist details.');
  }

  #reCalcCheckout(payload?: CalcCheckoutPayload) {
    const { hotels, tourists, reservationId } = this.formGroup.getRawValue();

    return this.#calcCheckout({
      hotels: this.#withExtensionChoice(hotels),
      reservationId,
      tourists,
      transferHotelCode: this.transferHotelCode(),
      ...payload
    } as CalcCheckoutPayload);
  }

  // #endregion

  // #region Form Initialization

  /**
   * R-AT6: a later calculation can bring transfer blocks the first one did not have (avia only: the blocks appear once "Where to?" names a
   * hotel) or drop them (the hotel cleared). Each tourist's transfer controls follow the blocks, so the Transfers dialog lists the cars and
   * Register sends the choice - before, avia only kept the empty list of the first calculation and the dialog showed no car (dev 09.10.2026).
   */
  #syncTransferControls(response: CheckoutFormOut): void {
    (response.tourists ?? []).forEach((tourist, index) => {
      const transfers = this.touristsFormArray.controls[index]?.controls.transfers;
      if (!transfers) return;
      // R-BIH42 (website-web#84): the same remove-from-the-end loop as the children ages, so it never aims below 0
      const wanted = Math.max(tourist.transfers?.length ?? 0, 0);
      while (transfers.length < wanted) transfers.push(new FormGroup(new CheckoutTouristTransferTypedForm()));
      while (transfers.length > wanted) transfers.removeAt(transfers.length - 1);
    });
  }

  #addFormArraysBasedOnData(): void {
    const checkoutFormOut = this.#checkoutFormOut();
    if (!checkoutFormOut) return;
    if (checkoutFormOut.tourists?.length) {
      // R-CK9a: every tourist starts from the signed-in contact's country (token), never the fixed site default
      const defaultCitizenship = this.#store.selectSnapshot(UserState.defaultCitizenship);
      checkoutFormOut.tourists.forEach((tourist) => {
        const touristForm = new FormGroup(new CheckoutTouristTypedForm(defaultCitizenship));
        if (tourist?.transfers?.length)
          tourist.transfers.forEach(() =>
            touristForm.controls.transfers.push(new FormGroup(new CheckoutTouristTransferTypedForm()))
          );
        this.formGroup.controls.tourists.push(touristForm);
      });
    }
    if (checkoutFormOut.hotels?.length) {
      for (const hotel of checkoutFormOut.hotels) {
        const hotelFormGroup = new FormGroup(new CheckoutHotelTypedForm());
        if (hotel.touristIds)
          hotel.touristIds.forEach(() => hotelFormGroup.controls.touristIds.push(new FormControl()));
        this.formGroup.controls.hotels.push(hotelFormGroup);
      }
    }
  }

  #resetForm(): void {
    this.#instantHotels.set({});
    this.instantContinueOnRequest.set({});
    this.#aviaTotalsSeen.set({});
    this.#aviaTotalsAccepted.set({});
    this.formGroup.controls.tourists.clear();
    this.formGroup.controls.hotels.clear();
    this.formGroup.reset();
  }

  #updateValidators(): void {
    for (const touristForm of this.touristsFormArray.controls) {
      const simplifiedFormForCitizenshipOf = this.simplifiedFormForCitizenshipOf();

      const hasSimplifiedForm = environment.disableSimplifiedCheckoutTouristForm
        ? false
        : simplifiedFormForCitizenshipOf.some((e) => e.countryCode === touristForm.controls.citizenship.value);

      //
      this.#applyTouristsFlightsValidators({ touristForm, hasSimplifiedForm });
      this.#applyInnValidators(touristForm);

      if (hasSimplifiedForm) {
        this.#applySimplifiedFormForCitizenshipOf({ touristForm });
      } else {
        this.#applyFullFormForCitizenshipOf({ touristForm });
      }
    }
  }

  #applyTouristsFlightsValidators({
    touristForm,
    hasSimplifiedForm
  }: {
    touristForm: FormGroup<CheckoutTouristTypedForm>;
    hasSimplifiedForm: boolean;
  }): void {
    if (!touristForm.controls.flights.value?.length) {
      const touristId = touristForm.controls.id.value;
      if (touristId) {
        // flightsIsRequired = the Arrival / Departure section is SHOWN: it holds the Terminal, which every transfer line needs (R-HT3)
        // ECI/LCO V1: a ticked early check-in whose landing is unknown (ECI_LANDING_UNKNOWN) shows the block too, to type the flight
        const showFlights =
          (this.#hasTransferLines(touristId) || this.#landingTourists().has(touristId)) && !hasSimplifiedForm;
        this.flightsIsRequired.update((data) => {
          data[touristId] = showFlights;
          return data;
        });
        // R-HT27: the flight details are required only for a car the agent chose (optional / mandatory); a leg with no car and a
        // car the destination includes (the server chose it) ask for none
        const carChosen = this.#getTransferQuantity(touristId, { skipIncluded: true }) > 0;

        if (showFlights && carChosen) {
          // R-CK26 (website-web#122): the Flight Number is required in both blocks; the Terminal stays optional (R-HT3)
          touristForm.controls.arrivalFlightInfo.controls.flightNumber.setValidators([Validators.required]);
          touristForm.controls.departureFlightInfo.controls.flightNumber.setValidators([Validators.required]);
          touristForm.controls.arrivalFlightInfo.controls.date.setValidators([
            Validators.required,
            // R-CK26: AFTER today - the shared minDate accepts its own date (">="), so it gets tomorrow's midnight; today is refused
            minDate(startOfTomorrow(), 'Date must be after today'),
            maxDateThanField(
              touristForm.controls.departureFlightInfo.controls.date,
              'Date must be before departure date'
            )
          ]);
          touristForm.controls.arrivalFlightInfo.controls.time.setValidators([Validators.required, timeMaskFormat()]);
          touristForm.controls.departureFlightInfo.controls.date.setValidators([
            Validators.required,
            minDateThanField(touristForm.controls.arrivalFlightInfo.controls.date, 'Date must be after arrival date')
          ]);
          touristForm.controls.departureFlightInfo.controls.time.setValidators([Validators.required, timeMaskFormat()]);
        } else {
          touristForm.controls.arrivalFlightInfo.controls.flightNumber.clearValidators();
          touristForm.controls.departureFlightInfo.controls.flightNumber.clearValidators();
          touristForm.controls.arrivalFlightInfo.controls.date.clearValidators();
          touristForm.controls.arrivalFlightInfo.controls.time.clearValidators();
          touristForm.controls.departureFlightInfo.controls.date.clearValidators();
          touristForm.controls.departureFlightInfo.controls.time.clearValidators();
          // a hidden section keeps no time; a shown one keeps what the agent typed (optional, R-HT27)
          if (!showFlights) {
            touristForm.controls.arrivalFlightInfo.controls.time.reset();
            touristForm.controls.departureFlightInfo.controls.time.reset();
          } else {
            // website-web#125 / #126: nothing is REQUIRED here (no car chosen, or only an included one), but what the agent types is still
            // checked - an impossible time and a departure before the arrival are refused. Both validators pass an empty value.
            touristForm.controls.arrivalFlightInfo.controls.time.setValidators([timeMaskFormat()]);
            touristForm.controls.departureFlightInfo.controls.time.setValidators([timeMaskFormat()]);
            touristForm.controls.arrivalFlightInfo.controls.date.setValidators([
              maxDateThanField(
                touristForm.controls.departureFlightInfo.controls.date,
                'Date must be before departure date'
              )
            ]);
            touristForm.controls.departureFlightInfo.controls.date.setValidators([
              minDateThanField(touristForm.controls.arrivalFlightInfo.controls.date, 'Date must be after arrival date')
            ]);
          }
        }
        // a changed validator list is applied only when the control itself is re-checked - the group alone keeps its old status
        // (R-HT27: a car chosen after the checkout opened left the empty flight fields valid and Register sent them empty)
        for (const leg of [touristForm.controls.arrivalFlightInfo, touristForm.controls.departureFlightInfo]) {
          leg.controls.flightNumber.updateValueAndValidity({ emitEvent: false });
          leg.controls.date.updateValueAndValidity({ emitEvent: false });
          leg.controls.time.updateValueAndValidity({ emitEvent: false });
        }
        touristForm.updateValueAndValidity();
      }
    }
  }

  #disablePersonIdentifierValidations(touristForm: FormGroup<CheckoutTouristTypedForm>): void {
    touristForm.controls.passportIssue.removeValidators(Validators.required);
    touristForm.controls.passportIssue.updateValueAndValidity();
    touristForm.controls.passportExpiry.removeValidators(Validators.required);
    touristForm.controls.passportExpiry.updateValueAndValidity();
    touristForm.controls.tin.clearValidators();
    touristForm.controls.tin.updateValueAndValidity();
  }

  #applyInnValidators(touristForm: FormGroup<CheckoutTouristTypedForm>): void {
    const checkoutFormOut = this.#checkoutFormOut();
    if (!checkoutFormOut) return;
    const citizenIdentifier = this.requiredCitizenIdentifiers().find(
      (el) => el.countryCode === touristForm.controls.citizenship.value
    );
    if (!citizenIdentifier) {
      touristForm.controls.tin.clearValidators();
      touristForm.controls.tin.setValue(null);
    } else if (!checkoutFormOut.disablePersonIdentifierValidations) {
      touristForm.controls.tin.setValidators(this.#getInnValidatorByInnIdentifier(citizenIdentifier.identifierType));
    }

    if (checkoutFormOut.disablePersonIdentifierValidations) this.#disablePersonIdentifierValidations(touristForm);
    touristForm.controls.tin.updateValueAndValidity();
  }

  #applySimplifiedFormForCitizenshipOf({ touristForm }: { touristForm: FormGroup<CheckoutTouristTypedForm> }): void {
    touristForm.controls.passportIssue.removeValidators(Validators.required);
    touristForm.controls.passportIssue.updateValueAndValidity();
    touristForm.controls.passportExpiry.removeValidators(Validators.required);
    touristForm.controls.passportExpiry.updateValueAndValidity();
    touristForm.controls.passportNumber.removeValidators(Validators.required);
    touristForm.controls.passportNumber.updateValueAndValidity();
    touristForm.controls.tin.clearValidators();
    touristForm.controls.tin.updateValueAndValidity();
  }

  #applyFullFormForCitizenshipOf({ touristForm }: { touristForm: FormGroup<CheckoutTouristTypedForm> }): void {
    // addValidators keeps the issue date rules too (not after today, not before the birth date) - R-CK7, KZ-BUG-25 retest
    touristForm.controls.passportIssue.addValidators(Validators.required);
    touristForm.controls.passportIssue.updateValueAndValidity();
    // addValidators keeps the field rules of checkout.typed-forms.ts (number pattern, 6 months ahead, 10 years) - R-CK7
    touristForm.controls.passportExpiry.addValidators(Validators.required);
    touristForm.controls.passportExpiry.updateValueAndValidity();
    touristForm.controls.passportNumber.addValidators(Validators.required);
    touristForm.controls.passportNumber.updateValueAndValidity();
    touristForm.controls.tin.setValidators(Validators.required);
    touristForm.controls.tin.updateValueAndValidity();
    this.#applyInnValidators(touristForm); //
  }

  // #endregion

  // #region Private Methods

  #getInnValidatorByInnIdentifier(identifierType?: IdentifierTypeEnum) {
    switch (identifierType) {
      case IdentifierTypeEnum.TIN:
        return [Validators.required, requiredLength(12), justNumbers()];
      // website-web#124 (R-CK9): the AZ FIN is 7 Latin letters or digits, the GE personal number 11 digits - length alone let "ABCDEFGHIJK" and "!@#$" through
      case IdentifierTypeEnum.FIN:
        return [Validators.required, requiredLength(7), regExpPattern(FIN_PATTERN, FIN_MESSAGE)];
      case IdentifierTypeEnum.PersonalNumber:
        return [Validators.required, requiredLength(11), justNumbers()];
      default:
        return [Validators.required, requiredLength(7)];
    }
  }

  #confirmAndDestroy(message: string, confirmType: ConfirmType): void {
    this.#confirmService.confirm(message, confirmType).subscribe(() => {
      this.#resetStateAndDestroy();
      this.#storeActions.goPreviousPage();
    });
  }

  /** A valid calculation always carries at least one tourist (passengers and guests included). */
  #hasBookableContent(response: CheckoutFormOut): boolean {
    return !!response.tourists?.length;
  }

  /**
   * Nothing on this page can be booked any more, so the cart is dropped and the user is sent back
   * *before* the message is shown - the checkout must never stay open on an empty cart, no matter
   * how (or whether) the dialog gets closed.
   */
  #leaveOnPriceUnavailable(message: string | null, showMessage = true, activityId?: string | null): void {
    this.refreshPriceResultError.set(message ?? '');
    this.#resetStateAndDestroy();
    this.#storeActions.goPreviousPage();
    if (!showMessage) return;
    this.#confirmService.confirm(
      {
        title: message ?? undefined,
        message:
          'The cart has been cleared because the price is no longer available. Please add your items to the cart again.'
      },
      // the navigation above fires `popstate`, which would otherwise dismiss this dialog at once
      { type: 'error', closeOnNavigation: false, ...(activityId ? { activityId } : {}) }
    );
  }

  /**
   * ADLC-1 R-SS5 / AC-45 / AC-47 (QA doc features/flight-block-seat-shortage/README.md, error code FLIGHT_PRICE_OVERBOOKED): the text of a
   * refused checkout / registration of a show-only tour - TOUR_NO_SEATS_DETAILS with the short flight and the party of the cart's tour, in the
   * site language; null when the answer is not that refusal (or the cart holds no tour), so the existing message stays. Text only - no amount.
   */
  #noSeatsRefusalText(response: CheckoutFormOut | null | undefined): string | null {
    if (!isNoSeatsRefusal(response)) return null;
    // ADLC-1 R-SS7 / R-SS10: the limit over the block is reached (OVER_BLOCK_LIMIT), or a registration of an on-request cart is refused
    // FLIGHT_PRICE_OVERBOOKED - the seats over the block are gone. A CalcCheckout NO_SEATS keeps the slice-1 text (error table, R-SS7a).
    if (
      response?.noMoreAvailableReason === OVER_BLOCK_LIMIT_REASON ||
      (response?.errorCode === FLIGHT_PRICE_OVERBOOKED && this.onRequestStep())
    )
      return this.#translate.instant(ON_REQUEST_NO_LONGER_TEXT);
    const tours = this.#selectors
      .cartItems()
      .filter((item) => item.type === 'tour')
      .map((item) => item.data);
    const tour = refusedTour(tours);
    return tour ? this.#translate.instant(TOUR_NO_SEATS_DETAILS_KEY, noSeatsTextParams(tour)) : null;
  }

  /**
   * ADLC-1 R-SS7a (error code FLIGHT_SEATS_ON_REQUEST_NOT_ACCEPTED): nothing was registered. The tick is cleared, the step stays on (the answer's
   * warning), the error dialog shows the reason with the Trace ID, and closing it brings the agent to the box (scroll, pulse, focus).
   */
  #showOnRequestNotAccepted(response: CheckoutFormOut | null | undefined, activityId: string | null | undefined): void {
    this.#onRequestRefusal.set(
      hasOnRequestWarning(response) ? response!.flightSeatsWarning! : { code: FLIGHT_SEATS_ON_REQUEST }
    );
    this.onRequestAccepted.set(false);
    this.#confirmService
      .confirm(this.#translate.instant(ON_REQUEST_NOT_ACCEPTED_TEXT), {
        type: 'error',
        activityId: activityId ?? undefined
      })
      .subscribe(() => this.onRequestFocusFn?.());
  }

  /** The no-seats refusal of a registration: nothing was registered, the checkout stays open so the agent can go to Other flights. */
  #showNoSeatsRefusal(text: string, activityId: string | null | undefined): void {
    this.#confirmService.confirm(text, { type: 'error', activityId: activityId ?? undefined });
  }

  /** A re-calculation refused NO_SEATS: the cart is dropped and the agent goes back as for any price that is gone, the text with its Trace ID. */
  #leaveOnNoSeats(response: CheckoutFormOut, text: string): void {
    this.#leaveOnPriceUnavailable(text, true, response.traceId ?? response.activityId);
  }

  #resetStateAndDestroy(): void {
    this.destroy();
    this.#storeActions.clearFormData();
    this.#storeActions.cartClear();
  }

  async #handleRefreshPriceResult(
    response: CheckoutFormOut,
    showSucccessMessage = false,
    recalc = false
  ): Promise<void> {
    if (!response?.refreshPriceResult) return;
    // a registered answer always opens its reservation - the no-seats exit is for a refused (re-)calculation only
    const noSeats = isRegisteredAnswer(response) ? null : this.#noSeatsRefusalText(response);
    if (noSeats) return this.#leaveOnNoSeats(response, noSeats);

    const checkPriceChangeChange = !this.#destroyListeners();

    const action = await this.#commonHelpersService.priceConfirmProcess(
      response?.refreshPriceResult,
      this.formGroup.controls.totalPrice.value ?? 0,
      response?.totalPrice ?? 0,
      response?.priceCurrency,
      checkPriceChangeChange,
      false,
      response?.excursions,
      // C23: a refused answer (e.g. EXCURSION_PRICE_CHANGED at Confirm, R-EUI41) shows its Trace ID in the error window
      response?.traceId ?? response?.activityId
    );

    if (action === 'accept' || action === null) {
      if (action === null) if (recalc) this.#reCalcCheckout().subscribe();
      if (showSucccessMessage) {
        // R-41: a seat the registration left out (hold ran out, no rate, seat service down) - the booking stands, the partner is told
        if (response.seats?.leftOut?.length)
          await firstValueFrom(
            this.#confirmService.confirm(
              'Some seats could not be added to the reservation - you can buy them later in the reservation',
              'warn'
            )
          );
        if (response.reservationId && !response.reservationDetailsUnavailable)
          this.#navigateToReservation(response.reservationId);
        else this.#confirmAndDestroy('Booking confirmed', 'success');
      }
    } else if (action === 'reload') {
      // priceConfirmProcess has already shown the message, so only tear the checkout down here
      this.#leaveOnPriceUnavailable(
        this.#commonHelpersService.getPriceUnavailableMessage(response.refreshPriceResult, response.excursions),
        false
      );
    }
  }

  /**
   * R-8: keeps what the latest refresh said about each hotel's instant confirmation. A price instant again drops the agent's earlier
   * "Continue on request" for it (as the approved prototype does); a response without `hotels` leaves the state as it was.
   */
  #applyInstantState(response: CheckoutFormOut): void {
    if (!response?.hotels) return;
    const next: Record<string, Pick<CheckoutHotelOut, 'instantConfirmation' | 'instantLost'>> = {};
    for (const hotel of response.hotels)
      if (hotel?.hotelPriceId)
        next[hotel.hotelPriceId] = {
          instantConfirmation: !!hotel.instantConfirmation,
          instantLost: hotel.instantLost ?? null
        };
    this.#instantHotels.set(next);
    this.instantContinueOnRequest.update((continued) => {
      const kept: Record<string, boolean> = {};
      for (const id of Object.keys(continued)) if (continued[id] && next[id]?.instantLost) kept[id] = true;
      return kept;
    });
  }

  /** R-10: `continueOnRequest` per hotel of the Confirm request. */
  #withInstantChoice<T extends { hotelPriceId?: string | null }>(
    hotels: T[] | null | undefined
  ): (T & { continueOnRequest: boolean })[] {
    const continued = this.instantContinueOnRequest();
    return (hotels ?? []).map((hotel) => ({ ...hotel, continueOnRequest: !!continued[hotel.hotelPriceId ?? ''] }));
  }

  #getTransferQuantity(touristId?: string | null, { skipIncluded = false }: { skipIncluded?: boolean } = {}): number {
    if (typeof touristId === 'undefined' || touristId === null) return 0;
    const tourists = this.#checkoutFormOut()?.tourists;
    const transfers = tourists?.find((e) => e.id === touristId)?.transfers;

    if (!transfers?.length) return 0;
    // R-HT27: a leg counts only with a real car chosen - no choice (null) and the "WITHOUT" placeholder are no transfer
    const withCar = transfers.filter((item) => {
      if (skipIncluded && item.requirement === 'included') return false;
      const car = item.transferPrices?.find((el) => el.id === item.selectedTransferPriceId);
      return !!car && !car.name?.includes('WITHOUT');
    });

    return withCar.length;
  }

  /** The tourist has transfer lines to give an airport for (the "WITHOUT" placeholder chosen = none). */
  #hasTransferLines(touristId: string): boolean {
    const transfers = this.#checkoutFormOut()?.tourists?.find((e) => e.id === touristId)?.transfers;
    return !!transfers?.some(
      (item) => !item.transferPrices?.find((el) => el.id === item.selectedTransferPriceId)?.name?.includes('WITHOUT')
    );
  }
  #mapHotelsData(
    hotels: (ICheckoutHotelFormValues & { _formIndex: number })[],
    touristsList: ICheckoutTouristFormValues[]
  ) {
    const allHotels = this.#selectors.cartHotels();
    if (!allHotels?.length) return null;

    const result: ICheckoutHotelData[] = [];

    hotels?.forEach((h) => {
      const hotel = allHotels.find((ah) => ah?.hotelPriceId === h?.hotelPriceId);
      if (hotel) {
        const tourists = touristsList?.filter((t) => h.touristIds?.includes(t.id!)) ?? [];
        result.push({ ...hotel, _formIndex: h._formIndex, tourists });
      }
    });
    return result;
  }
  #mapTouristsWithTransfersData(touristsWithTransferData: ICheckoutTouristFormValues[]) {
    return (
      touristsWithTransferData?.map((e) => ({
        ...e,
        transfersCount: this.#getTransferQuantity(e.id)
      })) ?? null
    );
  }
  #mapTouristsWithFlightsData(touristsWithFlightData: ICheckoutTouristFormValues[]) {
    const allFlights = this.#selectors.cartFlights();
    if (!allFlights?.length) return null;

    return touristsWithFlightData.flatMap((t) =>
      (t.flights ?? [])
        .map((e) => ({
          id: t.id!,
          firstName: t.firstName!,
          lastName: t.lastName!,
          flightNote: e.note!,
          flight: allFlights.find((f) => f.id === e.flightId)
        }))
        .filter((entry): entry is typeof entry & { flight: PackageFlight } => !!entry.flight)
    );
  }

  #mapPricesData(prices: Price[]) {
    return prices.map(({ agentCommissionAmount, agentCommissionPercent, totalPrice, type }) => {
      let service = '';
      if (type === PriceTypeEnum.OnlyAvia) service = 'Only tickets';
      else if (type === PriceTypeEnum.OnlyHotel) service = 'Hotels';
      else if (type === PriceTypeEnum.Package) service = 'Tours';
      else if (type === PriceTypeEnum.OnlyExcursion) service = 'Excursions';
      else if (type === PriceTypeEnum.Transfers)
        service = 'Transfers'; // R-HT15: mandatory / optional show the transfer as its own line
      else if (type === PriceTypeEnum.Insurance)
        service = 'Insurance'; // R-8b: the stay's insurance is its own row, as on the confirmation
      else if (type === PriceTypeEnum.Total) service = 'Total amount';
      return {
        service,
        type,
        agentCommissionAmount,
        agentCommissionPercent: type === PriceTypeEnum.Total && prices.length > 1 ? undefined : agentCommissionPercent,
        totalPrice,
        amountToPay: (totalPrice ?? 0) - (agentCommissionAmount ?? 0)
      };
    });
  }

  #navigateToReservation(reservationId: string): void {
    this.#resetStateAndDestroy();
    this.#storeActions.navigate(['/checkout/reservation', reservationId]);
  }

  // #endregion
}
